import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { apiError } from "@/lib/apiError";
import { toast } from "sonner";
import {
  ArrowLeft, KeyRound, ShieldCheck, Loader2, ExternalLink,
  CheckCircle2, AlertTriangle, XCircle, Copy, Eye, EyeOff,
} from "lucide-react";

/* Meta Cloud API — Dedicated onboarding page.
 *
 * Single-purpose: paste 3 values (Phone Number ID, WABA ID, Permanent Token),
 * we validate them against Meta's Graph API in real time, then persist to
 * BOTH storage shapes so every sender path finds the credentials.
 *
 * Route:  /app/whatsapp/connect-meta
 * Entry:  Prominent CTA on /app/whatsapp AND on /app/whatsapp/settings
 */

function Field({ label, hint, value, onChange, placeholder, secret, testid, monospaced = true }) {
  const [show, setShow] = useState(false);
  const isSecret = secret && !show;
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-[11px] uppercase tracking-widest font-medium text-ink">{label}</label>
        {secret && (
          <button type="button" onClick={() => setShow((s) => !s)}
                  className="text-[10px] text-ink-mid hover:text-ink inline-flex items-center gap-1"
                  data-testid={`${testid}-toggle-visibility`}>
            {show ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
            {show ? "Hide" : "Show"}
          </button>
        )}
      </div>
      <input
        type={isSecret ? "password" : "text"}
        value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        autoComplete="off" autoCorrect="off" spellCheck={false}
        data-testid={testid}
        className={`w-full rounded-xl border border-[#E7E1D3] bg-white px-4 py-3 text-sm text-ink focus:border-ink focus:outline-none focus:ring-2 focus:ring-ink/10 ${monospaced ? "font-mono" : ""}`} />
      {hint && <div className="mt-1.5 text-[11px] text-ink-mid leading-relaxed">{hint}</div>}
    </div>
  );
}

export default function ConnectMeta() {
  const navigate = useNavigate();
  const [phoneNumberId, setPhoneNumberId] = useState("");
  const [wabaId, setWabaId] = useState("");
  const [accessToken, setAccessToken] = useState("");
  const [displayPhone, setDisplayPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!phoneNumberId.trim() || !wabaId.trim() || !accessToken.trim()) {
      toast.error("Phone Number ID, WABA ID, and Access Token are all required");
      return;
    }
    if (!/^\d+$/.test(phoneNumberId.trim())) {
      toast.error("Phone Number ID must be all digits");
      return;
    }
    if (!/^\d+$/.test(wabaId.trim())) {
      toast.error("WABA ID must be all digits");
      return;
    }
    if (accessToken.trim().length < 20) {
      toast.error("Access token looks too short — permanent tokens start with EAA…");
      return;
    }

    setSubmitting(true);
    setResult(null);
    try {
      const { data } = await api.post("/whatsapp/connect-meta", {
        phone_number_id: phoneNumberId.trim(),
        waba_id:         wabaId.trim(),
        access_token:    accessToken.trim(),
        display_phone:   displayPhone.trim() || null,
      });
      setResult({ ok: true, ...data });
      if ((data.warnings || []).length === 0) {
        toast.success("Meta Cloud API connected — you can send messages now");
      } else {
        toast.warning("Credentials saved with warnings — see details below");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setResult({ ok: false, detail, raw: e?.response?.data });
      toast.error(typeof detail === "string" ? detail : detail?.message || apiError(e, "Meta rejected the credentials"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-6 py-8 space-y-6" data-testid="connect-meta-page">
      <div>
        <Link to="/app/whatsapp"
              className="inline-flex items-center gap-1.5 text-xs text-ink-mid hover:text-ink"
              data-testid="connect-meta-back">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to WhatsApp
        </Link>
        <h1 className="mt-3 font-display text-3xl text-ink">Connect Meta Cloud API</h1>
        <p className="mt-1 text-sm text-ink-mid max-w-2xl">
          Paste the three values from Meta Developer Portal → WhatsApp → API Setup. We validate
          the token against Meta's Graph API before storing so you know immediately whether it works.
        </p>
      </div>

      {/* Guide box */}
      <div className="rounded-2xl border border-[#E7E1D3] bg-[#FAF7F0] p-5 text-sm text-ink space-y-2"
           data-testid="connect-meta-guide">
        <div className="font-medium">Where to find these three values</div>
        <ol className="list-decimal ml-5 space-y-1.5 text-ink-mid text-[13px]">
          <li>
            Open <a href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer"
                    className="text-clay underline decoration-dotted inline-flex items-center gap-0.5">
              developers.facebook.com/apps<ExternalLink className="w-3 h-3" /></a>
            and open your WhatsApp app.
          </li>
          <li>
            In the left sidebar, click <b>WhatsApp → API Setup</b>.
          </li>
          <li>
            <b>Phone Number ID</b> and <b>WhatsApp Business Account ID</b> are shown at the top of that page — copy both.
          </li>
          <li>
            For the <b>Permanent Access Token</b>: create or use an existing System User at
            <a href="https://business.facebook.com/settings/system-users" target="_blank" rel="noreferrer"
               className="text-clay underline decoration-dotted inline-flex items-center gap-0.5 ml-1">
              business.facebook.com<ExternalLink className="w-3 h-3" /></a>
            with `whatsapp_business_messaging` + `whatsapp_business_management` permissions on your WABA. The
            token starts with <code className="text-[11px] bg-white border border-[#E7E1D3] rounded px-1">EAA…</code>.
          </li>
        </ol>
      </div>

      {/* Form */}
      <div className="rounded-3xl border border-[#E7E1D3] bg-white p-6 space-y-5"
           data-testid="connect-meta-form">
        <Field
          label="Phone Number ID *"
          hint="Numeric ID — usually 15-16 digits. This is NOT your phone number."
          value={phoneNumberId}
          onChange={setPhoneNumberId}
          placeholder="e.g. 123456789012345"
          testid="cm-phone-number-id" />
        <Field
          label="WhatsApp Business Account ID (WABA) *"
          hint="Numeric ID — usually 15-16 digits. Sits above the Phone Number ID on the API Setup page."
          value={wabaId}
          onChange={setWabaId}
          placeholder="e.g. 987654321098765"
          testid="cm-waba-id" />
        <Field
          label="Permanent Access Token *"
          hint="System User token (never expires). Starts with 'EAA…'. Keep this secret."
          value={accessToken}
          onChange={setAccessToken}
          placeholder="EAAG..."
          secret
          testid="cm-access-token" />
        <Field
          label="Display phone (optional)"
          hint="Only for showing in the dashboard. E.g. +91 98104 53892."
          value={displayPhone}
          onChange={setDisplayPhone}
          placeholder="+91 98104 53892"
          monospaced={false}
          testid="cm-display-phone" />

        <div className="flex items-center justify-between gap-3 pt-2 border-t border-[#E7E1D3]">
          <div className="text-[11px] text-ink-mid flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5" />
            Token is validated against Meta before it's stored. Never shown after save.
          </div>
          <button onClick={submit} disabled={submitting}
                  className="inline-flex items-center gap-2 text-sm font-medium text-white bg-ink hover:bg-black px-6 py-2.5 rounded-full transition-colors disabled:opacity-50"
                  data-testid="cm-submit-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            {submitting ? "Verifying with Meta…" : "Verify & Connect"}
          </button>
        </div>
      </div>

      {/* Result */}
      {result?.ok && <SuccessCard data={result} onGoToInbox={() => navigate("/app/whatsapp/inbox")} />}
      {result && !result.ok && <ErrorCard data={result} />}
    </div>
  );
}

function SuccessCard({ data, onGoToInbox }) {
  const warns = data.warnings || [];
  const tone = warns.length ? "amber" : "emerald";
  const Icon = warns.length ? AlertTriangle : CheckCircle2;
  return (
    <div className={`rounded-3xl border p-6 space-y-4 ${tone === "emerald"
        ? "border-emerald-200 bg-emerald-50"
        : "border-amber-200 bg-amber-50"}`}
         data-testid="cm-success-card">
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-full grid place-items-center shrink-0 ${
            tone === "emerald" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-800"}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="font-display text-xl text-ink">
            {warns.length ? "Saved with warnings" : "Connected to Meta Cloud API"}
          </div>
          <div className="mt-1 text-sm text-ink-mid">{data.next_step}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <Row label="Verified name"   value={data.verified_name || "—"} />
        <Row label="Display phone"   value={data.display_phone || "—"} mono />
        <Row label="Phone Number ID" value={data.phone_number_id} mono />
        <Row label="WABA ID"         value={data.waba_id} mono />
        <Row label="Quality"         value={data.quality_rating || "—"} />
        <Row label="Platform"        value={data.platform_type || "not registered"} tone={data.platform_type === "CLOUD_API" ? "good" : "warn"} />
        <Row label="Verification"    value={data.code_verification_status || "unknown"} tone={data.code_verification_status === "VERIFIED" ? "good" : "warn"} />
        <Row label="Graph version"   value={data.graph_version} mono />
      </div>

      {warns.length > 0 && (
        <div className="rounded-xl bg-white border border-amber-200 p-4 space-y-2"
             data-testid="cm-warnings">
          <div className="text-xs font-medium text-amber-900 uppercase tracking-wider">Warnings from Meta</div>
          {warns.map((w, i) => (
            <div key={i} className="text-sm text-ink flex gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" /> <span>{w}</span>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-2">
        <button onClick={onGoToInbox}
                className="inline-flex items-center gap-2 text-sm font-medium text-white bg-ink hover:bg-black px-4 py-2 rounded-full"
                data-testid="cm-go-to-inbox">
          Open Inbox
        </button>
        <Link to="/app/whatsapp/settings"
              className="inline-flex items-center gap-2 text-sm font-medium text-ink bg-white border border-[#E7E1D3] hover:border-ink px-4 py-2 rounded-full"
              data-testid="cm-go-to-settings">
          Go to settings
        </Link>
      </div>
    </div>
  );
}

function ErrorCard({ data }) {
  const d = data.detail;
  const message = typeof d === "string" ? d : d?.message;
  const graph_error = typeof d === "object" ? d?.graph_error : null;
  return (
    <div className="rounded-3xl border border-rose-200 bg-rose-50 p-6 space-y-3"
         data-testid="cm-error-card">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-full bg-rose-100 text-rose-700 grid place-items-center shrink-0">
          <XCircle className="w-5 h-5" />
        </div>
        <div>
          <div className="font-display text-xl text-ink">Meta rejected the credentials</div>
          <div className="mt-1 text-sm text-ink-mid">{message || "See raw response below."}</div>
        </div>
      </div>
      <pre className="text-[11px] bg-white border border-rose-200 rounded-xl p-3 overflow-x-auto max-h-[300px] text-ink-mid"
           data-testid="cm-error-raw">
        {JSON.stringify(graph_error || data.raw || data, null, 2)}
      </pre>
      <div className="text-[11px] text-ink-mid">
        Common causes: (a) the token was minted from a different Meta App than the one that owns the phone,
        (b) the Phone Number ID was pasted where the WABA ID belongs, (c) the System User doesn't have
        `whatsapp_business_messaging` permission on this WABA.
      </div>
    </div>
  );
}

function Row({ label, value, mono, tone }) {
  const toneClass = tone === "good" ? "text-emerald-700"
                  : tone === "warn" ? "text-amber-800"
                  : "text-ink";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-ink-mid">{label}</div>
      <div className={`mt-0.5 text-sm ${mono ? "font-mono" : ""} ${toneClass}`}>{value}</div>
    </div>
  );
}
